import DownloadIcon from "@mui/icons-material/Download";
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { useAtom } from "jotai/index";
import {
  MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useIsOwnerOrSuperAdmin } from "@/auth/hooks";
import { useTableSettings } from "@/hooks/use-table-settings";
import { useGetHandExportJobs } from "@/module/service-configs-module/statistics-visualization-module/hand-history-exports/hooks/use-get-hand-export-jobs";
import { BackgroundJobDto } from "@/shared/api";
import { useApi } from "@/shared/api/use-api";
import { meStore } from "@/store/store";

// Вкладка со списком джоб экспорта раздач: сами джобы ставятся из модалки
// «выгрузить раздачи» на панели фильтров, здесь — их статусы и скачивание
// готовых архивов (по свежей presigned-ссылке из GetBackgroundJob).
export const HandHistoryExportsPage = () => {
  const { t } = useTranslation();
  const { statsControllerApi } = useApi();
  const isOwner = useIsOwnerOrSuperAdmin();
  const [me] = useAtom(meStore);
  const [isShowOtherUserJobs, setIsShowOtherUserJobs] = useState(false);
  const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);

  const { defaultProps, state } = useTableSettings<BackgroundJobDto>("handHistoryExports", {
    pageSize: 10,
  });

  const { data: jobsResponse, isLoading } = useGetHandExportJobs({
    userId: isShowOtherUserJobs ? undefined : me?.id,
    pageNumber: state.pagination.pageIndex,
    pageSize: state.pagination.pageSize,
  });

  const handleDownload = async (job: BackgroundJobDto) => {
    if (downloadingJobId) {
      return;
    }

    try {
      setDownloadingJobId(job.job_id);
      // Ссылка в списке не отдаётся и быстро протухает — берём свежую.
      const { data } = await statsControllerApi.getBackgroundJob(job.user_id, job.job_id);
      const downloadUrl = data.job.download_url;
      if (!downloadUrl) {
        enqueueSnackbar(t("somethingWentWrong"), {
          variant: "error",
          autoHideDuration: 2000,
        });
        return;
      }

      const a = document.createElement("a");
      a.style.display = "none";
      a.href = downloadUrl;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      const message =
        (e as unknown as AxiosError<{ message: string }>)?.response?.data?.message || e;

      enqueueSnackbar(message as string, {
        variant: "error",
        autoHideDuration: 2000,
      });
    } finally {
      setDownloadingJobId(null);
    }
  };

  const columns = useMemo<MRT_ColumnDef<BackgroundJobDto>[]>(() => {
    return [
      {
        header: t("jobId"),
        accessorKey: "job_id",
        id: "job_id",
      },
      {
        header: t("status"),
        accessorKey: "status",
        id: "status",
        Cell: ({ row: { original } }) => {
          switch (original.status) {
            case "FAILED":
              return (
                <Tooltip title={original.error || ""}>
                  <span>{t("dataProcessingError")}</span>
                </Tooltip>
              );
            case "EXPIRED":
              return t("expired");
            case "DONE":
              return t("ready");
            case "PENDING":
            case "RUNNING":
            case "FINALIZING":
              return (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <span>
                    {t("created")} {original.tasks_done}/{original.tasks_total}
                  </span>
                  <CircularProgress size={16} />
                </Box>
              );
            default:
              return original.status;
          }
        },
      },
      {
        header: t("userId"),
        accessorKey: "user_id",
        id: "user_id",
      },
      {
        header: t("createdAt"),
        accessorKey: "created_at",
        id: "created_at",
        Cell: ({ row: { original } }) =>
          original.created_at ? dayjs(original.created_at).format("DD.MM.YYYY HH:mm") : "-",
      },
      {
        header: t("finishedAt"),
        accessorKey: "finished_at",
        id: "finished_at",
        Cell: ({ row: { original } }) =>
          original.finished_at ? dayjs(original.finished_at).format("DD.MM.YYYY HH:mm") : "-",
      },
      {
        header: t("download"),
        id: "download",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row: { original } }) => {
          if (original.status !== "DONE") {
            return null;
          }

          return downloadingJobId === original.job_id ? (
            <CircularProgress size={20} />
          ) : (
            <IconButton
              size="small"
              disabled={!!downloadingJobId}
              onClick={() => handleDownload(original)}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          );
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, downloadingJobId]);

  const table = useMaterialReactTable({
    data: jobsResponse?.data?.jobs || [],
    columns,
    getRowId: (originalRow) => originalRow.job_id,
    ...defaultProps,
    initialState: {
      ...defaultProps.initialState,
    },
    enableColumnOrdering: true,
    enableColumnDragging: true,
    manualPagination: true,
    rowCount: jobsResponse?.data.pagination.items_total,
    state: {
      ...state,
      isLoading,
      pagination: {
        pageSize: jobsResponse?.data.pagination.page_size || state.pagination.pageSize,
        pageIndex: jobsResponse?.data.pagination.page_number || state.pagination.pageIndex,
      },
    },
    renderTopToolbarCustomActions: () => {
      return isOwner ? (
        <FormControlLabel
          control={
            <Checkbox
              checked={isShowOtherUserJobs}
              onChange={(e) => setIsShowOtherUserJobs(e.target.checked)}
            />
          }
          label={t("showOtherUserSessions")}
        />
      ) : null;
    },
  });

  return (
    <Box sx={{ pt: 2, pb: 2 }}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Alert severity="info">{t("handExportsHint")}</Alert>
        </Grid>
        <Grid size={12}>
          <MaterialReactTable table={table} />
        </Grid>
      </Grid>
    </Box>
  );
};
